/**
 * ForceVane — dual CTA for the sticky add-to-cart bar.
 *
 * Why this exists: quantity on this product is owned by Kaching Bundles, not the theme.
 * The theme's quantity stepper updated its own display but its value was discarded at
 * add-to-cart (stepper at 3 added 1 unit), and the button price is derived from the
 * selected bundle tier rather than from quantity. Two controls for the same thing.
 *
 * This replaces the stepper with one button per tier. Each button selects its Kaching
 * tier and then triggers the theme's own add-to-cart button, so the existing add path
 * is reused rather than reimplemented. Kaching stays the single source of truth for
 * both quantity and price.
 *
 * Fails safe: if the tiers cannot be found, the native button is left visible and
 * untouched, so the product remains purchasable.
 */
(function () {
  var TIER_WRAP = '.kaching-bundles__bar-wrapper';
  var SETTLE_MS = 1200;

  function tierCards() {
    return Array.prototype.filter.call(
      document.querySelectorAll(TIER_WRAP),
      function (el) { return /Buy\s*\d/i.test(el.innerText || ''); }
    );
  }

  function cardForTier(n) {
    var re = new RegExp('Buy\\s*' + n + '(?!\\d)', 'i');
    // Prefer the deepest card that still carries a price, to avoid outer duplicates.
    var matches = tierCards().filter(function (el) { return re.test(el.innerText || ''); });
    for (var i = 0; i < matches.length; i++) {
      if (priceOf(matches[i])) return matches[i];
    }
    return matches[0] || null;
  }

  /* Price extraction mirrors the existing button-price script so the two never disagree. */
  function extractLowestPrice(text) {
    text = text
      .replace(/\$\d+(?:\.\d{2})?\s*(?:each|\/\s*each|off)/gi, '')
      .replace(/just\s*\$\d+(?:\.\d{2})?/gi, '');
    var prices = text.match(/\$\d+(?:\.\d{2})?/g);
    if (!prices) return null;
    return prices.map(function (p) { return parseFloat(p.slice(1)); }).sort(function (a, b) { return a - b; })[0];
  }

  function priceOf(card) {
    if (!card) return null;
    var el = card.querySelector('[class*="price"]:not([class*="full"]):not([class*="compare"]):not([class*="was"])');
    if (el) {
      var m = (el.innerText || '').match(/\$\d+(?:\.\d{2})?/);
      if (m) return parseFloat(m[0].slice(1));
    }
    var clone = card.cloneNode(true);
    clone.querySelectorAll('s, del, strike, [style*="line-through"], [class*="full-price"], [class*="compare"], [class*="was"], [class*="badge"], [class*="save"]')
      .forEach(function (n) { n.remove(); });
    return extractLowestPrice(clone.innerText || '');
  }

  function fmt(p) { return '• $' + (p % 1 === 0 ? p.toFixed(0) : p.toFixed(2)); }

  function isSelected(card) {
    if (!card) return false;
    if (card.querySelector('input[type="radio"]:checked')) return true;
    if (card.querySelector('[aria-checked="true"]')) return true;
    return /selected/i.test(card.className || '') || !!card.querySelector('[class*="selected"]');
  }

  /* Kaching does not respond to a bare .click() on the radio, so send a full pointer
     sequence at the wrapper, which is what a real tap produces. */
  function pressCard(card) {
    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach(function (type) {
      var Ctor = (type.indexOf('pointer') === 0 && window.PointerEvent) ? PointerEvent : MouseEvent;
      card.dispatchEvent(new Ctor(type, { bubbles: true, cancelable: true, view: window }));
    });
  }

  function selectTier(n) {
    return new Promise(function (resolve) {
      var card = cardForTier(n);
      if (!card) return resolve(false);
      if (isSelected(card)) return resolve(true);
      pressCard(card);
      var t0 = Date.now();
      (function poll() {
        var c = cardForTier(n);
        if (c && isSelected(c)) return resolve(true);
        if (Date.now() - t0 > SETTLE_MS) return resolve(false);
        setTimeout(poll, 60);
      })();
    });
  }

  function initBar(bar) {
    if (bar.dataset.fvDualCtaReady === '1') return;
    var wrap = bar.querySelector('[data-fv-dual-cta]');
    var native = bar.querySelector('button[name="add"]');
    if (!wrap || !native) return;

    var buttons = Array.prototype.slice.call(wrap.querySelectorAll('[data-fv-tier]'));

    function sync() {
      var ok = true;
      buttons.forEach(function (btn) {
        var price = priceOf(cardForTier(btn.dataset.fvTier));
        if (price == null) { ok = false; return; }
        var out = btn.querySelector('[data-fv-price]');
        var next = fmt(price);
        if (out.textContent !== next) out.textContent = next;
      });
      if (ok && wrap.hidden) {
        wrap.hidden = false;
        native.style.display = 'none';   // kept in the DOM: it is what actually adds
        bar.dataset.fvDualCtaReady = '1';
      }
      // Mirror the native button's availability.
      buttons.forEach(function (btn) {
        if (native.disabled) btn.setAttribute('disabled', 'disabled');
        else btn.removeAttribute('disabled');
      });
      return ok;
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.hasAttribute('disabled')) return;
        buttons.forEach(function (b) { b.setAttribute('disabled', 'disabled'); });
        selectTier(btn.dataset.fvTier).then(function () {
          native.click();
          setTimeout(function () {
            buttons.forEach(function (b) { b.removeAttribute('disabled'); });
          }, 1200);
        });
      });
    });

    if (!sync()) {
      // Kaching renders late; keep trying briefly, then give up and leave the native button.
      var tries = 0;
      var iv = setInterval(function () {
        if (sync() || ++tries > 40) clearInterval(iv);
      }, 250);
    }

    var mo = new MutationObserver(function () { sync(); });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  function start() {
    document.querySelectorAll('.m-sticky-addtocart').forEach(initBar);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
